import React, { useCallback } from "react";

import { faGripVertical } from "@fortawesome/pro-solid-svg-icons/faGripVertical";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Switch from "antd/es/switch";
import cn from "classnames";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { TextField } from "@mapmycustomers/ui";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import DeleteButton from "@app/scene/settings/component/ActivityTypes/component/DeleteButton";

import styles from "../ActivityTypesCommon.module.scss";

import ActivityTypeColorDropdown from "./ActivityTypeColorDropdown";
import DisableButton from "./DisableButton";

interface Props {
  activityType: ActivityType;
  className?: string;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  index: number;
  isDisabled?: boolean;
  onActivate: (id: ActivityType["id"], active: boolean) => void;
  onChangeColor?: (activityType: ActivityType, color: AnyColor | undefined) => void;
  onRename?: (activityType: ActivityType, newName: string) => void;
  onToggleCheckIn?: (activityType: ActivityType, isCheckIn: boolean) => void;
}

const ActivityTypeItem: React.FC<Props> = ({
  activityType,
  className,
  dragHandleProps,
  isDisabled,
  onActivate,
  onChangeColor,
  onRename,
  onToggleCheckIn,
}) => {
  const handleChangeActivityTypeName = useCallback(
    (name: string) => onRename?.(activityType, name),
    [activityType, onRename]
  );

  const handleToggleCheckIn = useCallback(
    (toggle: boolean) => onToggleCheckIn?.(activityType, toggle),
    [activityType, onToggleCheckIn]
  );

  const handleChangeColor = useCallback(
    (color: AnyColor | undefined) => {
      onChangeColor?.(activityType, color);
    },
    [activityType, onChangeColor]
  );

  return (
    <div className={className}>
      <div {...dragHandleProps}>
        <FontAwesomeIcon
          className={cn(styles.dragIcon, { [styles.hidden]: isDisabled })}
          icon={faGripVertical}
        />
      </div>
      <TextField
        className={styles.input}
        disabled={activityType.isDefault}
        onChange={handleChangeActivityTypeName}
        prefix={
          <ActivityTypeIcon
            activityType={activityType}
            activityTypeIconClassName={styles.typeIcon}
            size="sm"
          />
        }
        suffix={
          !activityType.isDefault && (
            <ActivityTypeColorDropdown
              onChange={handleChangeColor}
              value={activityType.color ?? undefined}
            />
          )
        }
        type="text"
        value={activityType.name}
      />
      <div className={styles.toggleCheckIn}>
        {activityType.active && (
          <Switch
            checked={activityType.checkInEnabled}
            className={styles.switch}
            onChange={handleToggleCheckIn}
          />
        )}
        {!activityType.active || activityType.isDefault ? (
          <DisableButton activityType={activityType} onActivate={onActivate} />
        ) : (
          <DeleteButton activityType={activityType} />
        )}
      </div>
    </div>
  );
};

export default ActivityTypeItem;
